/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Skeleton,
  SkeletonText,
  Spacer,
  Text,
  Textarea,
  Tooltip,
  useBoolean,
  useDisclosure,
  useMediaQuery,
  VStack,
} from '@chakra-ui/react';
import dateFormat from 'dateformat';
import isValidNip from 'is-valid-nip';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaEnvelope, FaExclamationCircle } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import i18n from '../../translations/i18n';
import DataGrid from '../core/components/data-grid';
import { fetchContractorFromGusAsync, selectContractor, selectContractorView } from '../features/account';
import apiClient from '../features/api-client';
import { selectIsBusy } from '../features/app';
import {
  createInvoiceCorrectionAsync,
  deleteLastInvoiceAsync,
  handleNSEIInvoiceAsync,
  handleNSEIInvoiceRefreshStatusAsync,
  loadData,
  loadNSEIInvoiceInfoAsync,
  loadNSEILighthouseStatusAsync,
  openInvoiceAsync,
  openInvoicesReportAsync,
  openNseiInvoiceAsync,
  openNseiUpoAsync,
  processNSEIInvoiceAutoAsync,
  selectAllRowsCount,
  selectLoadedDataExists,
  selectNSEIInvoiceInfo,
  selectNSEILighthouseStatus,
  selectSelectedEmailSent,
  selectSelectedId,
  selectSelectedNo,
  selectSortInfo,
  selectSummary,
  setNSEIInvoiceInfo,
  setSelectedEmailSent,
  setSelectedId,
  setSelectedNo,
} from '../features/invoices';
import { selectAccountRoles } from '../features/session';
import { showToast, TOAST_TYPE } from '../features/toast';
import { includesSome } from '../utils';

const dateFormatMask = 'yyyy-mm-dd';

const paymentMethodTypeIdToUiValue = (paymentMethodTypeId) => {
  switch (paymentMethodTypeId) {
    case 1:
      return 'Cash';
    case 4:
      return 'Card';
    case 3:
      return 'P7';
    case 2:
      return 'P14';
    case 5:
      return 'P';
    case 6:
      return 'Stripe';
    case 7:
      return 'MinikasaCash';
    case 8:
      return 'MinikasaTerminal';
    default:
      return 'Cash';
  }
};

const parseInvoiceNseiData = (invoiceNseiData) => {
  if (invoiceNseiData == null) {
    return null;
  }
  if (typeof invoiceNseiData === 'string') {
    try {
      const parsedData = JSON.parse(invoiceNseiData);
      if (parsedData.invoiceStatusDetails != null && typeof parsedData.invoiceStatusDetails === 'string') {
        return { ...parsedData, invoiceStatusDetails: JSON.parse(parsedData.invoiceStatusDetails) };
      }
      return parsedData;
    } catch {
      return null;
    }
  }
  return invoiceNseiData;
};

const isInvoiceInKsef = (nseiInfo) =>
  nseiInfo?.invoiceNseiStatus === 0 && nseiInfo?.parsedInvoiceNseiData?.invoiceStatus === 200;

const NSEIInvoiceInfo = ({ invoiceId, emailSent, onButtonClick, isCorrectionInvoice }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const nseiInvoiceInfo = useSelector(selectNSEIInvoiceInfo);
  const nseiLighthouseStatus = useSelector(selectNSEILighthouseStatus);
  const selectedInvoiceNo = useSelector(selectSelectedNo);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingUpo, setIsDownloadingUpo] = useState(false);
  const [isDownloadingInvoice, setIsDownloadingInvoice] = useState(false);
  const [isSendingByEmail, setIsSendingByEmail] = useState(false);
  const [loadedInvoiceId, setLoadedInvoiceId] = useState(null);
  const [correctedInvoiceNseiInfo, setCorrectedInvoiceNseiInfo] = useState(null);
  const [isLoadingCorrectedNsei, setIsLoadingCorrectedNsei] = useState(false);
  useEffect(() => {
    if (invoiceId != null) {
      if (loadedInvoiceId !== invoiceId) {
        dispatch(setNSEIInvoiceInfo(null));
        setLoadedInvoiceId(null);
      }
      dispatch(loadNSEIInvoiceInfoAsync({ invoiceId }));
    } else {
      dispatch(setNSEIInvoiceInfo(null));
      setLoadedInvoiceId(null);
    }

    dispatch(loadNSEILighthouseStatusAsync());
  }, [invoiceId, dispatch]);
  useEffect(() => {
    if (!isCorrectionInvoice || invoiceId == null) {
      setCorrectedInvoiceNseiInfo(null);
      setIsLoadingCorrectedNsei(false);
      return undefined;
    }

    let cancelled = false;
    const loadCorrectedInvoiceNseiInfo = async () => {
      setIsLoadingCorrectedNsei(true);
      setCorrectedInvoiceNseiInfo(null);
      try {
        const invoiceResponse = await apiClient.get(`/invoices/${invoiceId}`);
        const correctedInvoiceId = invoiceResponse.data?.invoice_id;
        if (correctedInvoiceId == null) {
          return;
        }
        const [correctedInvoiceResponse, nseiResponse] = await Promise.all([
          apiClient.get(`/invoices/${correctedInvoiceId}`),
          apiClient.get(`/invoices/${correctedInvoiceId}/nsei`),
        ]);
        if (cancelled) {
          return;
        }
        setCorrectedInvoiceNseiInfo({
          invoiceNo: correctedInvoiceResponse.data?.invoice_no ?? '',
          parsedInvoiceNseiData: parseInvoiceNseiData(nseiResponse.data.invoiceNseiData),
          invoiceNseiStatus: nseiResponse.data.invoiceNseiStatus,
        });
      } finally {
        if (!cancelled) {
          setIsLoadingCorrectedNsei(false);
        }
      }
    };

    loadCorrectedInvoiceNseiInfo();
    return () => {
      cancelled = true;
    };
  }, [isCorrectionInvoice, invoiceId]);
  useEffect(() => {
    if (nseiInvoiceInfo != null && invoiceId != null && loadedInvoiceId !== invoiceId) {
      setLoadedInvoiceId(invoiceId);
    }
  }, [nseiInvoiceInfo, invoiceId, loadedInvoiceId]);
  const handleNSEIInvoiceClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    setIsSending(true);
    dispatch(
      handleNSEIInvoiceAsync({
        invoiceId,
        onSuccess: () => {
          setIsSending(false);
          dispatch(loadNSEIInvoiceInfoAsync({ invoiceId }));
        },
      }),
    );
  };
  const handleNSEIInvoiceRefreshStatusClick = () => {
    if (onButtonClick) {
      onButtonClick();
    }
    setIsRefreshing(true);
    dispatch(
      handleNSEIInvoiceRefreshStatusAsync({
        invoiceId,
        onSuccess: () => {
          setIsRefreshing(false);
          dispatch(loadNSEIInvoiceInfoAsync({ invoiceId }));
        },
      }),
    );
  };
  const handleDownloadUpoClick = () => {
    setIsDownloadingUpo(true);
    dispatch(
      openNseiUpoAsync({
        invoiceId,
        format: 'pdf',
        onSuccess: () => {
          setIsDownloadingUpo(false);
        },
      }),
    );
  };

  const handleDownloadInvoiceAndSendByEmail = (sendByEmail = false) => {
    if (sendByEmail) {
      setIsSendingByEmail(true);
    } else {
      setIsDownloadingInvoice(true);
    }

    dispatch(
      openNseiInvoiceAsync({
        invoiceId,
        format: 'pdf',
        sendByEmail,
        onSuccess: () => {
          if (sendByEmail) {
            setIsSendingByEmail(false);
          } else {
            setIsDownloadingInvoice(false);
          }
        },
      }),
    );
  };
  const handleDownloadInvoiceClick = () => {
    handleDownloadInvoiceAndSendByEmail(false);
  };
  const handleSendByEmailClick = () => {
    handleDownloadInvoiceAndSendByEmail(true);
  };

  const isLoading = invoiceId != null && (nseiInvoiceInfo == null || loadedInvoiceId !== invoiceId);
  const isCorrectionInKsef = isInvoiceInKsef(nseiInvoiceInfo);
  const isCorrectedInvoiceInKsef = isInvoiceInKsef(correctedInvoiceNseiInfo);
  const isCorrectionSendBlocked =
    isCorrectionInvoice &&
    !isLoadingCorrectedNsei &&
    correctedInvoiceNseiInfo != null &&
    !isCorrectedInvoiceInKsef &&
    !isCorrectionInKsef;
  const isContentLoading = isLoading || (isCorrectionInvoice && isLoadingCorrectedNsei);

  return (
    <VStack spacing={4} align="stretch" w="full">
      <Alert status={nseiLighthouseStatus?.status === 'AVAILABLE' ? 'success' : 'error'} justifyContent="center">
        <AlertIcon />
        <AlertTitle>{t('modules.invoices.NSEILighthouseStatusTitle', 'NSEI status')}</AlertTitle>
        {nseiLighthouseStatus?.status === 'AVAILABLE' && (
          <AlertTitle>{t('modules.invoices.NSEILighthouseStatusAvailable', 'AVAILABLE')}</AlertTitle>
        )}
        {nseiLighthouseStatus?.status === 'MAINTENANCE' && (
          <AlertTitle>{t('modules.invoices.NSEILighthouseStatusMaintenance', 'MAINTENANCE')}</AlertTitle>
        )}
        {nseiLighthouseStatus?.status === 'FAILURE' && (
          <AlertTitle>{t('modules.invoices.NSEILighthouseStatusFailure', 'FAILURE')}</AlertTitle>
        )}
        {nseiLighthouseStatus?.status === 'TOTAL_FAILURE' && (
          <AlertTitle>{t('modules.invoices.NSEILighthouseStatusTotalFailure', 'TOTAL FAILURE')}</AlertTitle>
        )}
      </Alert>
      {isContentLoading && (
        <Alert
          status="info"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          borderRadius="md"
          p={6}
          w="full"
        >
          <Skeleton boxSize="40px" borderRadius="full" mb={4} />
          <SkeletonText mt={4} mb={1} noOfLines={1} spacing={4} width="60%" />
          <SkeletonText mt={2} noOfLines={2} spacing={4} width="80%" />
        </Alert>
      )}
      {!isContentLoading && isCorrectionSendBlocked && (
        <VStack spacing={4} align="stretch">
          <Alert
            status="warning"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            p={6}
            w="full"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t('modules.invoices.CorrectionNSEIBlockedTitle', 'Cannot send correction to KSeF')}
            </AlertTitle>
            <AlertDescription maxWidth="xl">
              {t(
                'modules.invoices.CorrectionNSEIBlockedDescription',
                'A correction invoice can only be sent to KSeF if the corrected invoice {{correctedInvoiceNo}} is already registered in KSeF.',
                { correctedInvoiceNo: correctedInvoiceNseiInfo?.invoiceNo ?? '' },
              )}
            </AlertDescription>
          </Alert>
          <HStack w="full">
            <Button w="full" disabled colorScheme="green">
              {t('modules.invoices.NSEISend', 'Send to NSEI')}
            </Button>
            <Button w="full" disabled colorScheme="blue">
              {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
            </Button>
          </HStack>
        </VStack>
      )}
      {!isContentLoading &&
        !isCorrectionSendBlocked &&
        nseiInvoiceInfo?.invoiceNseiStatus === 0 &&
        nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatus === 200 && (
          <VStack spacing={4} align="stretch">
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="md"
              p={6}
              w="full"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                {t('modules.invoices.InvoiceInNSEISucceedTitle', 'Invoice {{invoiceNo}} is in KSeF', {
                  invoiceNo: selectedInvoiceNo || '',
                })}
              </AlertTitle>
              <AlertDescription maxWidth="xl">
                {t(
                  'modules.invoices.InvoiceInNSEIDescription',
                  'The invoice has been sent to KSeF. You can verify it by scanning the QR code. You can also verify the issuer by scanning the certificate QR code.',
                )}
              </AlertDescription>
              {(nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrCert ||
                nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrLink) && (
                <VStack spacing={2} mt={4}>
                  <HStack spacing={4} justifyContent="center">
                    {nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrLink && (
                      <VStack spacing={2}>
                        <Text fontSize="sm" fontWeight="bold">
                          {t('modules.invoices.QRCodeVerification', 'INVOICE VERIFICATION')}
                        </Text>
                        <a
                          href={nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrLink)}`}
                            alt="Invoice QR Code"
                            style={{ maxWidth: '200px', height: 'auto' }}
                          />
                        </a>
                      </VStack>
                    )}
                    {nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrCert && (
                      <VStack spacing={2}>
                        <Text fontSize="sm" fontWeight="bold">
                          {t('modules.invoices.QRCodeCertificate', 'CERTIFICATE')}
                        </Text>
                        <a
                          href={nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrCert}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ cursor: 'pointer' }}
                        >
                          <img
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrCert)}`}
                            alt="Certificate QR Code"
                            style={{ maxWidth: '200px', height: 'auto' }}
                          />
                        </a>
                      </VStack>
                    )}
                  </HStack>
                  <Text fontSize="xs" mt={2} textAlign="center">
                    {t('modules.invoices.QRCodeClickHint', "Can't scan the code? Click it")}
                  </Text>
                </VStack>
              )}
            </Alert>
            <HStack w="full">
              <Button
                onClick={handleDownloadUpoClick}
                disabled={
                  invoiceId == null ||
                  nseiInvoiceInfo?.invoiceNseiStatus !== 0 ||
                  isRefreshing ||
                  isDownloadingInvoice ||
                  isSendingByEmail ||
                  (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
                }
                colorScheme="blue"
                isLoading={isDownloadingUpo}
              >
                {t('modules.invoices.NSEIUpo', 'Download Receipt')}
              </Button>
              <ButtonGroup isAttached>
                <Button
                  onClick={handleDownloadInvoiceClick}
                  disabled={
                    invoiceId == null ||
                    nseiInvoiceInfo?.invoiceNseiStatus !== 0 ||
                    isRefreshing ||
                    isDownloadingUpo ||
                    isSendingByEmail ||
                    (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
                  }
                  colorScheme="blue"
                  isLoading={isDownloadingInvoice}
                >
                  {t('modules.invoices.NSEIInvoice', 'Download Invoice')}
                </Button>
                <Tooltip label={t('modules.invoices.SendByEmail', 'Send by email')}>
                  <Button
                    onClick={handleSendByEmailClick}
                    disabled={
                      emailSent ||
                      invoiceId == null ||
                      nseiInvoiceInfo?.invoiceNseiStatus !== 0 ||
                      isRefreshing ||
                      isDownloadingUpo ||
                      isDownloadingInvoice ||
                      (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
                    }
                    colorScheme="blue"
                    flex={1}
                    isLoading={isSendingByEmail}
                  >
                    <Icon as={FaEnvelope} />
                  </Button>
                </Tooltip>
              </ButtonGroup>
              <Button
                flex={1}
                onClick={handleNSEIInvoiceRefreshStatusClick}
                disabled={
                  invoiceId == null ||
                  isDownloadingUpo ||
                  isDownloadingInvoice ||
                  isSendingByEmail ||
                  (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
                }
                colorScheme="blue"
                isLoading={isRefreshing}
              >
                {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
              </Button>
            </HStack>
          </VStack>
        )}
      {!isContentLoading &&
        !isCorrectionSendBlocked &&
        nseiInvoiceInfo?.invoiceNseiStatus === 0 &&
        nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatus !== 200 && (
          <VStack spacing={4} align="stretch">
            <Alert
              status="warning"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="md"
              p={6}
              w="full"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                {t('modules.invoices.InvoiceInNSEIErrorTitle', 'Invoice {{invoiceNo}} sending to KSeF error', {
                  invoiceNo: selectedInvoiceNo || '',
                })}
              </AlertTitle>
              <AlertDescription maxWidth="xl">
                {t(
                  'modules.invoices.InvoiceInNSEIDescriptionError',
                  'The invoice has been sent to KSeF error. Please try again later.',
                )}
              </AlertDescription>
              <Divider orientation="horizontal" my={4} />
              <Text fontSize="sm">{nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatusDetails?.description}</Text>
              <Divider orientation="horizontal" my={4} />
              {(nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrCert ||
                nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrLink) &&
                nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatus === 440 && (
                  <VStack spacing={2} mt={4}>
                    <HStack spacing={4} justifyContent="center">
                      {nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrLink && (
                        <VStack spacing={2}>
                          <Text fontSize="sm" fontWeight="bold">
                            {t('modules.invoices.QRCodeVerification', 'INVOICE VERIFICATION')}
                          </Text>
                          <a
                            href={nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ cursor: 'pointer' }}
                          >
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrLink)}`}
                              alt="Invoice QR Code"
                              style={{ maxWidth: '200px', height: 'auto' }}
                            />
                          </a>
                        </VStack>
                      )}
                      {nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceQrCert && (
                        <VStack spacing={2}>
                          <Text fontSize="sm" fontWeight="bold">
                            {t('modules.invoices.QRCodeCertificate', 'CERTIFICATE')}
                          </Text>
                          <a
                            href={nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrCert}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ cursor: 'pointer' }}
                          >
                            <img
                              src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(nseiInvoiceInfo.parsedInvoiceNseiData.invoiceQrCert)}`}
                              alt="Certificate QR Code"
                              style={{ maxWidth: '200px', height: 'auto' }}
                            />
                          </a>
                        </VStack>
                      )}
                    </HStack>
                    <Text fontSize="xs" mt={2} textAlign="center">
                      {t('modules.invoices.QRCodeClickHint', "Can't scan the code? Click it")}
                    </Text>
                  </VStack>
                )}
            </Alert>
            <HStack w="full">
              <Button
                w="full"
                onClick={handleNSEIInvoiceRefreshStatusClick}
                disabled={invoiceId == null || (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'}
                colorScheme="blue"
                isLoading={isRefreshing}
              >
                {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
              </Button>
            </HStack>
          </VStack>
        )}
      {!isContentLoading &&
        !isCorrectionSendBlocked &&
        nseiInvoiceInfo?.invoiceNseiStatus > 1 &&
        nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatus !== 200 && (
          <VStack spacing={4} align="stretch">
            <Alert
              status="error"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="md"
              p={6}
              w="full"
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                {t('modules.invoices.InvoiceInNSEIErrorTitle', 'Invoice {{invoiceNo}} sending to KSeF error', {
                  invoiceNo: selectedInvoiceNo || '',
                })}
              </AlertTitle>
              <AlertDescription maxWidth="xl">
                {t(
                  'modules.invoices.InvoiceInNSEIDescriptionProxyError',
                  'During communication with the KSeF Proxy Service an error occurred.',
                )}
              </AlertDescription>
              <Divider orientation="horizontal" my={4} />
              <Text fontSize="sm">{nseiInvoiceInfo?.parsedInvoiceNseiData?.invoiceStatusDetails?.description}</Text>
            </Alert>
            <HStack w="full">
              <Button
                w="full"
                onClick={handleNSEIInvoiceClick}
                disabled={
                  invoiceId == null ||
                  emailSent ||
                  isRefreshing ||
                  (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
                }
                colorScheme="green"
                isLoading={isSending}
              >
                {t('modules.invoices.NSEISend', 'Send to NSEI')}
              </Button>
              <Button
                w="full"
                onClick={handleNSEIInvoiceRefreshStatusClick}
                disabled={invoiceId == null || (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'}
                colorScheme="blue"
                isLoading={isRefreshing}
              >
                {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
              </Button>
            </HStack>
          </VStack>
        )}
      {!isContentLoading && !isCorrectionSendBlocked && nseiInvoiceInfo?.invoiceNseiStatus === 1 && (
        <VStack spacing={4} align="stretch">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            p={6}
            w="full"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t('modules.invoices.InvoiceInNSEIErrorTitle', 'Invoice {{invoiceNo}} sending to KSeF error', {
                invoiceNo: selectedInvoiceNo || '',
              })}
            </AlertTitle>
            <AlertDescription maxWidth="xl">
              {t(
                'modules.invoices.InvoiceInNSEIDescriptionError',
                'The invoice has been sent to KSeF error. Please try again later.',
              )}
            </AlertDescription>
            <Divider orientation="horizontal" my={4} />
            <Text fontSize="sm">{nseiInvoiceInfo?.invoiceNseiData}</Text>
          </Alert>
          <HStack w="full">
            <Button
              w="full"
              onClick={handleNSEIInvoiceClick}
              disabled={
                invoiceId == null ||
                emailSent ||
                isRefreshing ||
                (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'
              }
              colorScheme="green"
              isLoading={isSending}
            >
              {t('modules.invoices.NSEISendAgain', 'Send to NSEI again')}
            </Button>
            <Button
              w="full"
              onClick={handleNSEIInvoiceRefreshStatusClick}
              disabled={invoiceId == null || isSending || (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'}
              colorScheme="blue"
              isLoading={isRefreshing}
            >
              {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
            </Button>
          </HStack>
        </VStack>
      )}
      {!isContentLoading && !isCorrectionSendBlocked && nseiInvoiceInfo?.invoiceNseiStatus == null && (
        <VStack spacing={4} align="stretch">
          <Alert
            status="info"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            borderRadius="md"
            p={6}
            w="full"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              {t('modules.invoices.InvoiceInNSEIInfoTitle', 'Invoice {{invoiceNo}} not sent to KSeF', {
                invoiceNo: selectedInvoiceNo || '',
              })}
            </AlertTitle>
            <AlertDescription maxWidth="xl">
              {t(
                'modules.invoices.InvoiceInNSEIDescriptionInfo',
                'The invoice was not sent to KSeF. You can send it manually by clicking the button below.',
              )}
            </AlertDescription>
          </Alert>
          <HStack w="full">
            <Button
              onClick={handleNSEIInvoiceClick}
              disabled={invoiceId == null || emailSent || (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'}
              colorScheme="green"
              isLoading={isSending}
              isDisabled={isRefreshing}
              w="full"
            >
              {t('modules.invoices.NSEISend', 'Send to NSEI')}
            </Button>
            <Button
              w="full"
              onClick={handleNSEIInvoiceRefreshStatusClick}
              disabled={invoiceId == null || isSending || (nseiLighthouseStatus?.status ?? 'UNKNOWN') !== 'AVAILABLE'}
              colorScheme="blue"
              isLoading={isRefreshing}
            >
              {t('modules.invoices.NSEIRefreshStatus', 'Refresh NSEI status')}
            </Button>
          </HStack>
        </VStack>
      )}
    </VStack>
  );
};

const InvoiceCorrectionForm = forwardRef(({ invoiceId, selectedInvoiceData, isOpen }, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isBusy = useSelector(selectIsBusy);
  const [isLargerThan800] = useMediaQuery('(min-width: 800px)');
  const [correctionReason, setCorrectionReason] = useState('');
  const [vatin, setVatin] = useState('');
  const [name, setName] = useState('');
  const [address1, setAddress1] = useState('');
  const [address2, setAddress2] = useState('');
  const [amount, setAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [description, setDescription] = useState('');
  const [errors, setErrors] = useState({});

  const clearError = (field) => {
    setErrors((prev) => {
      if (prev[field] == null) {
        return prev;
      }
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateForm = useCallback(() => {
    const nextErrors = {};
    const requiredMessage = t(
      'pages.account.contractor.A value in this field is required',
      'A value in this field is required',
    );

    if (correctionReason.trim() === '') {
      nextErrors.correctionReason = requiredMessage;
    }

    if (vatin === '' || vatin == null) {
      nextErrors.vatin = requiredMessage;
    } else if (!/^\d+$/.test(vatin)) {
      nextErrors.vatin = t('pages.account.contractor.Vatin has wrong format', 'Vatin number has wrong format');
    } else if (!isValidNip(vatin)) {
      nextErrors.vatin = t('pages.account.contractor.Vatin is incorrect', 'Vatin number is incorrect');
    }

    if (name.trim() === '') {
      nextErrors.name = requiredMessage;
    }

    if (address1.trim() === '') {
      nextErrors.address1 = requiredMessage;
    }

    if (address2.trim() === '') {
      nextErrors.address2 = requiredMessage;
    }

    const amountValue = parseFloat(amount);
    if (amount === '' || amount == null || Number.isNaN(amountValue) || amountValue < 0) {
      nextErrors.amount = requiredMessage;
    }

    return nextErrors;
  }, [address1, address2, amount, correctionReason, name, t, vatin]);

  useImperativeHandle(
    ref,
    () => ({
      validateAndGetData: () => {
        const validationErrors = validateForm();
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) {
          return null;
        }

        return {
          correctionReason: correctionReason.trim(),
          typeId: 1,
          vatin,
          name: name.trim(),
          address1: address1.trim(),
          address2: address2.trim(),
          amount: parseFloat(amount),
          paymentMethod,
          description: description.trim(),
        };
      },
    }),
    [address1, address2, amount, correctionReason, description, name, paymentMethod, validateForm, vatin],
  );

  useEffect(() => {
    if (!isOpen || invoiceId == null) {
      return;
    }

    const loadFormData = async () => {
      setErrors({});
      setCorrectionReason('');
      setPaymentMethod('Cash');
      setDescription('');
      setAmount(parseFloat(selectedInvoiceData?.grossValue) || 0);

      if (selectedInvoiceData?.contractorId != null) {
        try {
          const response = await apiClient.get(`/contractors/${selectedInvoiceData.contractorId}`);
          const contractor = response.data;
          setVatin(contractor?.vatin ?? '');
          setName(contractor?.name ?? selectedInvoiceData?.contractorName ?? '');
          setAddress1(contractor?.address1 ?? '');
          setAddress2(contractor?.address2 ?? '');
        } catch {
          setVatin('');
          setName(selectedInvoiceData?.contractorName ?? '');
          setAddress1('');
          setAddress2('');
        }
      } else {
        setVatin('');
        setName(selectedInvoiceData?.contractorName ?? '');
        setAddress1('');
        setAddress2('');
      }
    };

    loadFormData();
  }, [isOpen, invoiceId, selectedInvoiceData]);

  const handleGetDataFromGusClick = () => {
    if (vatin === '' || vatin == null) {
      return;
    }

    dispatch(
      fetchContractorFromGusAsync({
        vatin,
        slim: isLargerThan800 === false,
        callback: (selectedCompany) => {
          setName(selectedCompany.name);
          setAddress1(selectedCompany.address1);
          setAddress2(selectedCompany.address2);
          clearError('name');
          clearError('address1');
          clearError('address2');
        },
      }),
    );
  };

  return (
    <VStack align="stretch" spacing={4}>
      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionReason', 'Correction reason')}
        </Heading>
        <Divider my={1} />
        <FormControl isInvalid={errors.correctionReason != null}>
          <Textarea
            size="sm"
            rows={2}
            focusBorderColor="brand.500"
            value={correctionReason}
            onChange={(event) => {
              setCorrectionReason(event.target.value);
              clearError('correctionReason');
            }}
          />
          <FormErrorMessage fontSize="xs">{errors.correctionReason}</FormErrorMessage>
        </FormControl>
      </Box>
      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionBuyer', 'Buyer')}
        </Heading>
        <Divider my={1} />
        <HStack align="flex-start">
          <FormControl maxWidth="125">
            <FormLabel fontSize="xs" mb={1}>
              {t('pages.account.contractor.Type', 'Type')}
            </FormLabel>
            <Select size="xs" focusBorderColor="brand.500" value="1" isDisabled>
              <option value="1">{t('pages.account.contractor.Company', 'Company')}</option>
            </Select>
          </FormControl>
          <FormControl flex={1} isInvalid={errors.vatin != null}>
            <FormLabel fontSize="xs" mb={1}>
              {t('pages.account.contractor.Vatin', 'Vatin')}
            </FormLabel>
            <HStack align="center">
              <Input
                size="xs"
                focusBorderColor="brand.500"
                value={vatin}
                isReadOnly
              />
              <Tooltip label={t('pages.account.contractor.GetFromRegon', 'retrieve data from regon database')}>
                <Button
                  size="xs"
                  h="28px"
                  minH="28px"
                  px={3}
                  colorScheme="silver"
                  w="62px"
                  minW="62px"
                  onClick={handleGetDataFromGusClick}
                  disabled={isBusy || vatin === '' || vatin == null}
                >
                  {t('pages.account.contractor.Gus', 'GUS')}
                </Button>
              </Tooltip>
            </HStack>
            <FormErrorMessage fontSize="xs">{errors.vatin}</FormErrorMessage>
          </FormControl>
        </HStack>
        <FormControl mt={2} isInvalid={errors.name != null}>
          <FormLabel fontSize="xs" mb={1}>
            {t('pages.account.contractor.Name', 'Name')}
          </FormLabel>
          <Input
            size="xs"
            focusBorderColor="brand.500"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              clearError('name');
            }}
            placeholder={t('pages.account.contractor.enter your new name', 'enter your new name')}
          />
          <FormErrorMessage fontSize="xs">{errors.name}</FormErrorMessage>
        </FormControl>
        <FormControl mt={2} isInvalid={errors.address1 != null}>
          <FormLabel fontSize="xs" mb={1}>
            {t('pages.account.contractor.Address1', 'Address1')}
          </FormLabel>
          <Input
            size="xs"
            focusBorderColor="brand.500"
            value={address1}
            onChange={(event) => {
              setAddress1(event.target.value);
              clearError('address1');
            }}
            placeholder={t('pages.account.contractor.enter your new address1', 'enter your new address1')}
          />
          <FormErrorMessage fontSize="xs">{errors.address1}</FormErrorMessage>
        </FormControl>
        <FormControl mt={2} isInvalid={errors.address2 != null}>
          <FormLabel fontSize="xs" mb={1}>
            {t('pages.account.contractor.Address2', 'Address2')}
          </FormLabel>
          <Input
            size="xs"
            focusBorderColor="brand.500"
            value={address2}
            onChange={(event) => {
              setAddress2(event.target.value);
              clearError('address2');
            }}
            placeholder={t('pages.account.contractor.enter your new address2', 'enter your new address2')}
          />
          <FormErrorMessage fontSize="xs">{errors.address2}</FormErrorMessage>
        </FormControl>
      </Box>
      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionAmountSection', 'Amount, payment method, description')}
        </Heading>
        <Divider my={1} />
        <HStack align="flex-start" spacing={3}>
          <FormControl isInvalid={errors.amount != null} w="90px" flexShrink={0}>
            <FormLabel fontSize="xs" mb={1}>
              {t('pages.funds.Amount', 'Amount')}
            </FormLabel>
            <NumberInput
              step={0.01}
              min={0}
              size="xs"
              focusBorderColor="brand.500"
              value={amount}
              onChange={(valueString) => {
                setAmount(valueString);
                clearError('amount');
              }}
            >
              <NumberInputField />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            <FormErrorMessage fontSize="xs">{errors.amount}</FormErrorMessage>
          </FormControl>
          <FormControl w="120px" flexShrink={0}>
            <FormLabel fontSize="xs" mb={1}>
              {t('pages.funds.PaymentMethod', 'Payment method')}
            </FormLabel>
            <Select
              focusBorderColor="brand.500"
              size="xs"
              value={paymentMethod}
              onChange={(event) => setPaymentMethod(event.target.value)}
            >
              <option value="Cash">{t('pages.funds.Cash', 'Cash')}</option>
              <option value="Card">{t('pages.funds.Card', 'Card')}</option>
              <option value="P7">{t('pages.funds.P7', 'Transfer 7 days')}</option>
              <option value="P14">{t('pages.funds.P14', 'Transfer 14 days')}</option>
            </Select>
          </FormControl>
          <FormControl flex={1}>
            <FormLabel fontSize="xs" mb={1}>
              {t('pages.funds.Description', 'Description')}
            </FormLabel>
            <Input
              focusBorderColor="brand.500"
              size="xs"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </FormControl>
        </HStack>
      </Box>
    </VStack>
  );
});

InvoiceCorrectionForm.displayName = 'InvoiceCorrectionForm';

const InvoiceCorrectionCompare = ({ corrected, correction, isLoading }) => {
  const { t } = useTranslation();

  const renderPaymentMethodLabel = (pmTypeId) => {
    const pm = paymentMethodTypeIdToUiValue(pmTypeId);
    switch (pm) {
      case 'Cash':
        return t('pages.funds.Cash', 'Cash');
      case 'Card':
        return t('pages.funds.Card', 'Card');
      case 'P7':
        return t('pages.funds.P7', 'Transfer 7 days');
      case 'P14':
        return t('pages.funds.P14', 'Transfer 14 days');
      case 'Stripe':
        return t('pages.funds.Stripe', 'Stripe');
      case 'MinikasaCash':
        return t('pages.funds.MinikasaCash', 'Minikasa cash');
      case 'MinikasaTerminal':
        return t('pages.funds.MinikasaTerminal', 'Minikasa terminal');
      default:
        return pm;
    }
  };

  const row = (label, was, is) => (
    <HStack align="flex-start" spacing={4}>
      <Text fontSize="xs" minW="120px" color="gray.500">
        {label}
      </Text>
      <Text fontSize="xs" flex={1}>
        {was ?? ''}
      </Text>
      <Text fontSize="xs" flex={1}>
        {is ?? ''}
      </Text>
    </HStack>
  );

  if (isLoading) {
    return (
      <VStack align="stretch" spacing={2}>
        <Skeleton height="16px" />
        <Skeleton height="16px" />
        <Skeleton height="16px" />
      </VStack>
    );
  }

  const wasBuyer = corrected?.saved_buyer ?? {};
  const isBuyer = correction?.saved_buyer ?? {};

  return (
    <VStack align="stretch" spacing={4}>
      <HStack spacing={4}>
        <Spacer />
        <Heading size="xs" flex={1}>
          {t('modules.invoices.CorrectionWas', 'Was')}
        </Heading>
        <Heading size="xs" flex={1}>
          {t('modules.invoices.CorrectionIs', 'Is')}
        </Heading>
      </HStack>

      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionReason', 'Correction reason')}
        </Heading>
        <Divider my={1} />
        {row(
          t('modules.invoices.CorrectionReason', 'Correction reason'),
          wasBuyer?.correctionReason ?? '',
          isBuyer?.correctionReason ?? '',
        )}
      </Box>

      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionBuyer', 'Buyer')}
        </Heading>
        <Divider my={1} />
        {row(t('pages.account.contractor.Vatin', 'Vatin'), wasBuyer?.vatin, isBuyer?.vatin)}
        {row(t('pages.account.contractor.Name', 'Name'), wasBuyer?.name, isBuyer?.name)}
        {row(t('pages.account.contractor.Address1', 'Address1'), wasBuyer?.address1, isBuyer?.address1)}
        {row(t('pages.account.contractor.Address2', 'Address2'), wasBuyer?.address2, isBuyer?.address2)}
      </Box>

      <Box>
        <Heading color="gray.500" size="xs">
          {t('modules.invoices.CorrectionAmountSection', 'Amount, payment method, description')}
        </Heading>
        <Divider my={1} />
        {row(
          t('pages.funds.Amount', 'Amount'),
          corrected?.gross_value != null ? Number(corrected.gross_value).toFixed(2) : '',
          correction?.gross_value != null ? Number(correction.gross_value).toFixed(2) : '',
        )}
        {row(
          t('pages.funds.PaymentMethod', 'Payment method'),
          renderPaymentMethodLabel(corrected?.payment_method_type_id),
          renderPaymentMethodLabel(correction?.payment_method_type_id),
        )}
        {row(t('pages.funds.Description', 'Description'), corrected?.description ?? '', correction?.description ?? '')}
      </Box>
    </VStack>
  );
};

const InvoicesFilterBar = ({
  filter,
  setFilter,
  contractorId,
  admin,
  reload,
  setReload,
  onModalClose,
  selectedInvoiceIsInNsei,
  selectedInvoiceTypeId,
  selectedInvoiceData,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isCorrectionOpen,
    onOpen: onCorrectionOpen,
    onClose: onCorrectionClose,
  } = useDisclosure();
  const {
    isOpen: isCorrectionShowOpen,
    onOpen: onCorrectionShowOpen,
    onClose: onCorrectionShowClose,
  } = useDisclosure();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [useFromDate, setUseFromDate] = useBoolean();
  const [useToDate, setUseToDate] = useBoolean();
  const [nseiError, setNseiError] = useBoolean();
  const [dateFrom, setDateFrom] = useState(dateFormat(Date.parse(Date.now()), dateFormatMask));
  const [dateTo, setDateTo] = useState(dateFormat(Date.parse(Date.now()), dateFormatMask));
  const [invoiceNo, setInvoiceNo] = useState('');
  const [contractorName, setContractorName] = useState('');
  const [source, setSource] = useState('');
  const [isDownloadingSelectedInvoice, setIsDownloadingSelectedInvoice] = useState(false);
  const [isNSEIAutoProcessing, setIsNSEIAutoProcessing] = useState(false);
  const [wasButtonClicked, setWasButtonClicked] = useState(false);
  const sortInfo = useSelector(selectSortInfo);
  const loadedDataExists = useSelector(selectLoadedDataExists);
  const selectedInvoiceId = useSelector(selectSelectedId);
  const selectedInvoiceNo = useSelector(selectSelectedNo);
  const selectedEmailSent = useSelector(selectSelectedEmailSent);
  const correctionFormRef = useRef(null);
  const [isCorrectionShowLoading, setIsCorrectionShowLoading] = useState(false);
  const [correctedInvoiceInfo, setCorrectedInvoiceInfo] = useState(null);
  const [correctionInvoiceInfo, setCorrectionInvoiceInfo] = useState(null);
  const handleClearFilters = () => {
    setUseFromDate.off();
    setUseToDate.off();
    setNseiError.off();
    setInvoiceNo('');
    setContractorName('');
    setSource('');
    setDateFrom(dateFormat(Date.parse(Date.now()), dateFormatMask));
    setDateTo(dateFormat(Date.parse(Date.now()), dateFormatMask));
    setFilter([]);
  };

  const handleApplyFilters = () => {
    let dateOperator = null;
    let dateValue = null;
    if (dateFrom !== '' && useFromDate && dateTo !== '' && useToDate) {
      dateOperator = 'inrange';
      dateValue = {
        start: dateFormat(Date.parse(dateFrom), dateFormatMask),
        end: dateFormat(Date.parse(dateTo), dateFormatMask),
      };
    } else if (dateFrom !== '' && useFromDate) {
      dateOperator = 'afterOrOn';
      dateValue = dateFormat(Date.parse(dateFrom), dateFormatMask);
    } else if (dateTo !== '' && useToDate) {
      dateOperator = 'beforeOrOn';
      dateValue = dateFormat(Date.parse(dateTo), dateFormatMask);
    } else {
      dateOperator = null;
    }

    const newFilter = [];

    if (useFromDate || useToDate) {
      newFilter.push({
        name: 'invoiceDate',
        operator: dateOperator,
        type: 'date',
        value: dateValue,
      });
    }

    if (invoiceNo !== '') {
      newFilter.push({
        name: 'invoiceNo',
        operator: 'contains',
        type: 'string',
        value: invoiceNo,
      });
    }

    if (contractorName !== '') {
      newFilter.push({
        name: 'contractorName',
        operator: 'contains',
        type: 'string',
        value: contractorName,
      });
    }

    if (source !== '') {
      newFilter.push({
        name: 'invoiceSource',
        operator: 'contains',
        type: 'string',
        value: source,
      });
    }

    if (nseiError) {
      newFilter.push({
        name: 'invoiceNseiStatus',
        operator: 'gt',
        type: 'number',
        value: 0,
      });
    }

    setFilter(newFilter);
  };
  const handleDownloadSelectedInvoiceClick = async () => {
    if (selectedInvoiceTypeId === 6 && !selectedInvoiceIsInNsei) {
      onCorrectionShowOpen();
      return;
    }
    setIsDownloadingSelectedInvoice(true);
    try {
      if (selectedInvoiceIsInNsei) {
        await dispatch(openNseiInvoiceAsync({ invoiceId: selectedInvoiceId, format: 'pdf' }));
        return;
      }
      await dispatch(openInvoiceAsync({ invoiceId: selectedInvoiceId, contractorId }));
    } finally {
      setIsDownloadingSelectedInvoice(false);
    }
  };
  const handleNSEISelectedInvoiceClick = (event) => {
    // Alt+Shift (Option+Shift on macOS) — avoids Ctrl+click context menu on macOS
    if (event?.altKey && event?.shiftKey) {
      const isNseiEmpty = selectedInvoiceData?.invoiceNseiStatus == null;
      const isEmailEmpty = !selectedEmailSent;
      if (!isNseiEmpty || !isEmailEmpty) {
        showToast({
          title: t('modules.invoices.NSEIAutoForbidden', 'Operation forbidden'),
          description: t(
            'modules.invoices.NSEIAutoForbiddenDescription',
            'Auto NSEI is allowed only when NSEI and Email columns are empty',
          ),
          type: TOAST_TYPE.INFO,
          duration: 8000,
        });
        return;
      }

      setIsNSEIAutoProcessing(true);
      dispatch(
        processNSEIInvoiceAutoAsync({
          invoiceId: selectedInvoiceId,
          isCorrectionInvoice: selectedInvoiceTypeId === 6,
          onSuccess: ({ shouldReload }) => {
            setIsNSEIAutoProcessing(false);
            if (shouldReload) {
              if (onModalClose) {
                onModalClose(selectedInvoiceId);
              }
              setReload((prev) => prev + 1);
            }
          },
        }),
      );
      return;
    }

    setWasButtonClicked(false);
    onOpen();
  };
  const handleModalClose = () => {
    if (onModalClose) {
      onModalClose(selectedInvoiceId);
    }
    onClose();
    if (wasButtonClicked) {
      setReload((prev) => prev + 1);
    }
    setWasButtonClicked(false);
  };
  const handleNSEIButtonClick = () => {
    setWasButtonClicked(true);
  };
  const handleDownloadInvoicesReportClick = () => {
    dispatch(openInvoicesReportAsync({ sortInfo, contractorId, filter }));
  };
  const handleDeleteLastInvoiceClick = () => {
    dispatch(deleteLastInvoiceAsync({ reload, setReload }));
  };
  const handleCorrectionClick = () => {
    onCorrectionOpen();
  };
  const handleCorrectionModalClose = () => {
    onCorrectionClose();
  };
  const handleCorrectionShowModalClose = () => {
    onCorrectionShowClose();
  };
  const handleCorrectionSaveChanges = () => {
    const data = correctionFormRef.current?.validateAndGetData();
    if (data == null || selectedInvoiceId == null) {
      return;
    }

    dispatch(
      createInvoiceCorrectionAsync({
        correctedInvoiceId: selectedInvoiceId,
        correctionReason: data.correctionReason,
        vatin: data.vatin,
        name: data.name,
        address1: data.address1,
        address2: data.address2,
        amount: data.amount,
        paymentMethod: data.paymentMethod,
        description: data.description,
        reload,
        setReload,
        onSuccess: () => {
          handleCorrectionModalClose();
        },
      }),
    );
  };

  useEffect(() => {
    const load = async () => {
      if (!isCorrectionShowOpen || selectedInvoiceId == null) {
        return;
      }
      setIsCorrectionShowLoading(true);
      setCorrectionInvoiceInfo(null);
      setCorrectedInvoiceInfo(null);
      try {
        const correctionResponse = await apiClient.get(`/invoices/${selectedInvoiceId}`);
        const correctionInvoice = correctionResponse.data;
        setCorrectionInvoiceInfo(correctionInvoice);
        const correctedId = correctionInvoice?.invoice_id;
        if (correctedId != null) {
          const correctedResponse = await apiClient.get(`/invoices/${correctedId}`);
          setCorrectedInvoiceInfo(correctedResponse.data);
        } else {
          setCorrectedInvoiceInfo(null);
        }
      } finally {
        setIsCorrectionShowLoading(false);
      }
    };
    load();
  }, [isCorrectionShowOpen, selectedInvoiceId]);
  const handleTodayClick = () => {
    setInvoiceNo('');
    setContractorName('');
    setSource('');
    setNseiError.off();
    const now = Date.now();
    const fromDate = dateFormat(Date.parse(now), dateFormatMask);
    const toDate = dateFormat(Date.parse(now), dateFormatMask);
    setDateFrom(fromDate);
    setDateTo(toDate);
    setUseFromDate.on();
    setUseToDate.on();
    setFilter([
      {
        name: 'invoiceDate',
        operator: 'inrange',
        type: 'date',
        value: {
          start: fromDate,
          end: toDate,
        },
      },
    ]);
  };
  const handleCurrentWeekClick = () => {
    setInvoiceNo('');
    setContractorName('');
    setSource('');
    setNseiError.off();
    const now = Date.now();
    let day = new Date().getDay();
    if (day === 0) {
      day = 6;
    } else {
      day -= 1;
    }
    const fromDate = dateFormat(Date.parse(new Date(now - day * 24 * 60 * 60 * 1000)), dateFormatMask);
    const toDate = dateFormat(Date.parse(now), dateFormatMask);
    setDateFrom(fromDate);
    setDateTo(toDate);
    setUseFromDate.on();
    setUseToDate.on();
    setFilter([
      {
        name: 'invoiceDate',
        operator: 'inrange',
        type: 'date',
        value: {
          start: fromDate,
          end: toDate,
        },
      },
    ]);
  };
  const handleCurrentMonthClick = () => {
    setInvoiceNo('');
    setContractorName('');
    setSource('');
    setNseiError.off();
    const now = Date.now();
    const day = new Date().getDate() - 1;
    const fromDate = dateFormat(Date.parse(new Date(now - day * 24 * 60 * 60 * 1000)), dateFormatMask);
    const toDate = dateFormat(Date.parse(now), dateFormatMask);
    setDateFrom(fromDate);
    setDateTo(toDate);
    setUseFromDate.on();
    setUseToDate.on();
    setFilter([
      {
        name: 'invoiceDate',
        operator: 'inrange',
        type: 'date',
        value: {
          start: fromDate,
          end: toDate,
        },
      },
    ]);
  };
  const handlePreviousMonthClick = () => {
    setInvoiceNo('');
    setContractorName('');
    setSource('');
    setNseiError.off();
    const date = new Date();
    const now = Date.now();
    const dayTo = new Date().getDate();
    const fromDate = dateFormat(Date.parse(new Date(date.getFullYear(), date.getMonth() - 1, 1)), dateFormatMask);
    const toDate = dateFormat(Date.parse(new Date(now - dayTo * 24 * 60 * 60 * 1000)), dateFormatMask);
    setDateFrom(fromDate);
    setDateTo(toDate);
    setUseFromDate.on();
    setUseToDate.on();
    setFilter([
      {
        name: 'invoiceDate',
        operator: 'inrange',
        type: 'date',
        value: {
          start: fromDate,
          end: toDate,
        },
      },
    ]);
  };
  return (
    <>
      <VStack alignSelf="stretch" alignContent="flex-start" alignItems="stretch" w="max-content">
        <HStack w="full">
          {admin && (
            <Tooltip
              label={t(
                'modules.invoices.NSEIAutoTooltip',
                'NSEI (Alt+Shift = auto send + email when NSEI and Email are empty)',
              )}
            >
              <Button
                flexShrink={0}
                size={admin ? 'sm' : undefined}
                minWidth={30}
                colorScheme="green"
                onClick={handleNSEISelectedInvoiceClick}
                disabled={selectedInvoiceId == null || isNSEIAutoProcessing}
                isLoading={isNSEIAutoProcessing}
              >
                {t('modules.invoices.NSEI', 'NSEI')}
              </Button>
            </Tooltip>
          )}
          <Tooltip label={t('modules.invoices.DownloadInvoicesReport', 'Download invoices report')} flex={1}>
            <Button
              w="full"
              size={admin ? 'sm' : undefined}
              minWidth={admin ? 75 : 107}
              colorScheme="brand"
              onClick={handleDownloadInvoicesReportClick}
              disabled={!loadedDataExists}
            >
              {t('modules.invoices.Report', 'Report')}
            </Button>
          </Tooltip>
          <Tooltip label={t('modules.invoices.DownloadSelectedInvoice', 'Download selected invoice')} flex={1}>
            <Button
              w="full"
              size={admin ? 'sm' : undefined}
              minWidth={admin ? 75 : 107}
              colorScheme="brand"
              onClick={handleDownloadSelectedInvoiceClick}
              disabled={selectedInvoiceId == null}
              isLoading={isDownloadingSelectedInvoice}
            >
              {t('modules.invoices.Show', 'Show')}
            </Button>
          </Tooltip>
        </HStack>
        {admin && (
          <HStack w="full">
            <Tooltip label={t('modules.invoices.DeleteLastTooltip', 'Delete last invoice from system')} flex={1}>
              <Button
                w="full"
                size="sm"
                colorScheme="silver"
                onClick={handleDeleteLastInvoiceClick}
                disabled={!loadedDataExists}
              >
                {t('modules.invoices.DeleteLast', 'Delete last')}
              </Button>
            </Tooltip>
            <Tooltip label={t('modules.invoices.CorrectionTooltip', 'Issue invoice correction')} flex={1}>
              <Button
                w="full"
                size="sm"
                colorScheme="yellow"
                onClick={handleCorrectionClick}
                disabled={selectedInvoiceId == null || selectedInvoiceTypeId === 6}
              >
                {t('modules.invoices.Correction', 'Correction')}
              </Button>
            </Tooltip>
          </HStack>
        )}
        <Heading disabled colorScheme="brand" size="sm">
          {t('modules.invoices.Filters', 'Filters')}
        </Heading>
        <Divider orientation="horizontal" />
        <HStack w="full">
          <Button flex={1} colorScheme="brand" size="sm" onClick={handleTodayClick}>
            {t('modules.invoices.Today', 'Today')}
          </Button>
          <Button flex={1} colorScheme="brand" size="sm" onClick={handleCurrentWeekClick}>
            {t('modules.invoices.CurrentWeek', 'Current week')}
          </Button>
        </HStack>
        <HStack alignSelf="stretch">
          <Button flex={1} whiteSpace="nowrap" colorScheme="brand" size="sm" onClick={handlePreviousMonthClick}>
            {t('modules.invoices.PreviousMonth', 'Previous month')}
          </Button>
          <Button flex={1} whiteSpace="nowrap" colorScheme="brand" size="sm" onClick={handleCurrentMonthClick}>
            {t('modules.invoices.CurrentMonth', 'Current month')}
          </Button>
        </HStack>
        <Divider orientation="horizontal" />
        <Checkbox colorScheme="brand" isChecked={useFromDate} onChange={setUseFromDate.toggle}>
          {t('modules.invoices.FromDate', 'From date')}
        </Checkbox>
        <Input
          focusBorderColor="brand.500"
          disabled={!useFromDate}
          w="full"
          type="date"
          size="xs"
          value={dateFrom}
          onChange={(event) => setDateFrom(event.currentTarget.value)}
        />
        <Checkbox colorScheme="brand" isChecked={useToDate} onChange={setUseToDate.toggle}>
          {t('modules.invoices.ToDate', 'To date')}
        </Checkbox>
        <Input
          focusBorderColor="brand.500"
          w="full"
          disabled={!useToDate}
          size="xs"
          type="date"
          value={dateTo}
          onChange={(event) => setDateTo(event.currentTarget.value)}
        />
        <Divider orientation="horizontal" />
        <Text size="xs">{t('modules.invoices.InvoiceNo', 'Invoice No')}</Text>
        <Input
          w="full"
          focusBorderColor="brand.500"
          size="xs"
          value={invoiceNo}
          onChange={(event) => setInvoiceNo(event.target.value)}
        />
        {admin && <Text size="xs">{t('modules.invoices.ContractorName', 'Contractor')}</Text>}
        {admin && (
          <Input
            w="full"
            focusBorderColor="brand.500"
            size="xs"
            value={contractorName}
            onChange={(event) => setContractorName(event.target.value)}
          />
        )}
        <Text size="xs">{t('modules.invoices.Source', 'Source')}</Text>
        <Input
          w="full"
          focusBorderColor="brand.500"
          size="xs"
          value={source}
          onChange={(event) => setSource(event.target.value)}
        />
        {admin && (
          <Checkbox colorScheme="brand" isChecked={nseiError} onChange={setNseiError.toggle}>
            {t('modules.invoices.NSEIError', 'NSEI Error')}
          </Checkbox>
        )}
        <Spacer />
        <Divider orientation="horizontal" />
        <Button w="full" colorScheme="brand" onClick={handleApplyFilters}>
          {t('modules.invoices.ApplyFilters', 'Apply filters')}
        </Button>
        <Button w="full" colorScheme="gray" onClick={handleClearFilters}>
          {t('modules.invoices.ClearFilters', 'Clear filters')}
        </Button>
      </VStack>
      <Modal onClose={handleModalClose} size="xl" isOpen={isOpen} isCentered scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent maxW="2xl">
          <ModalHeader>{t('modules.invoices.NSEI', 'NSEI')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6} px={6}>
            <NSEIInvoiceInfo
              invoiceId={selectedInvoiceId}
              emailSent={selectedEmailSent}
              onButtonClick={handleNSEIButtonClick}
              isCorrectionInvoice={selectedInvoiceTypeId === 6}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
      <Modal
        onClose={handleCorrectionModalClose}
        size="xl"
        isOpen={isCorrectionOpen}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="2xl">
          <ModalHeader>
            {t('modules.invoices.CorrectionInvoiceTitle', 'Invoice correction {{invoiceNo}}', {
              invoiceNo: selectedInvoiceNo ?? '',
            })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4} px={4}>
            <InvoiceCorrectionForm
              ref={correctionFormRef}
              invoiceId={selectedInvoiceId}
              selectedInvoiceData={selectedInvoiceData}
              isOpen={isCorrectionOpen}
            />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={handleCorrectionModalClose}>
              {t('modules.invoices.CorrectionCancel', 'Cancel')}
            </Button>
            <Button size="sm" colorScheme="brand" ml={3} onClick={handleCorrectionSaveChanges}>
              {t('modules.invoices.CorrectionSaveChanges', 'Save changes')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        onClose={handleCorrectionShowModalClose}
        size="xl"
        isOpen={isCorrectionShowOpen}
        isCentered
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent maxW="2xl">
          <ModalHeader>
            {t('modules.invoices.CorrectionInvoiceTitle', 'Invoice correction {{invoiceNo}}', {
              invoiceNo: correctedInvoiceInfo?.invoice_no ?? '',
            })}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4} px={4}>
            <InvoiceCorrectionCompare
              corrected={correctedInvoiceInfo}
              correction={correctionInvoiceInfo}
              isLoading={isCorrectionShowLoading}
            />
          </ModalBody>
          <ModalFooter>
            <Button size="sm" onClick={handleCorrectionShowModalClose}>
              {t('modules.invoices.CorrectionCancel', 'Cancel')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

const NSEIStatusRender = ({ value, parsedInvoiceNseiData }) => {
  if (value >= 1) {
    return <Icon as={FaExclamationCircle} color="red.500" boxSize={5} />;
  }

  if (value === 0) {
    if (parsedInvoiceNseiData != null) {
      if (parsedInvoiceNseiData.invoiceStatus === 200) {
        return <Icon as={FaCheckCircle} color="green.500" boxSize={5} />;
      } else {
        return <Icon as={FaExclamationCircle} color="yellow.500" boxSize={5} />;
      }
    }
  }

  return null;
};

const defaultColumns = ({ admin }) => [
  {
    name: 'index',
    type: 'number',
    header: i18n.t('modules.invoices.Index', 'No'),
    textAlign: 'end',
    maxWidth: 75,
    sortable: false,
  },
  {
    name: 'invoiceId',
    type: 'number',
    defaultVisible: false,
    header: i18n.t('modules.invoices.InvoiceId', 'Id'),
  },
  {
    name: 'invoiceNo',
    header: i18n.t('modules.invoices.InvoiceNo', 'Invoice Number'),
    defaultWidth: 180,
  },
  {
    name: 'correctedInvoiceNo',
    header: i18n.t('modules.invoices.CorrectedInvoiceNo', 'Corrected No.'),
    defaultWidth: 140,
  },
  {
    name: 'invoiceNseiStatus',
    header: i18n.t('modules.invoices.NseiStatus', 'NSEI'),
    allowUnsort: false,
    maxWidth: 80,
    defaultVisible: true,
    textAlign: 'center',
    render: ({ value, data }) => <NSEIStatusRender value={value} parsedInvoiceNseiData={data?.parsedInvoiceNseiData} />,
  },
  {
    name: 'emailSent',
    header: i18n.t('modules.invoices.EmailSent', 'Email'),
    allowUnsort: false,
    maxWidth: 80,
    defaultVisible: admin,
    textAlign: 'center',
    render: ({ value }) => (value ? <Icon as={FaCheckCircle} color="green.500" boxSize={5} /> : null),
  },
  { name: 'invoiceDate', header: i18n.t('modules.invoices.InvoiceDate', 'Invoice Date'), allowUnsort: false },
  {
    name: 'contractorId',
    type: 'number',
    defaultVisible: false,
    header: i18n.t('modules.invoices.ContractorId', 'Contractor Id'),
  },
  {
    name: 'contractorName',
    defaultFlex: 2,
    header: i18n.t('modules.invoices.ContractorName', 'Contractor'),
    defaultVisible: admin,
  },
  {
    name: 'invoiceSource',
    defaultFlex: 1,
    header: i18n.t('modules.invoices.Source', 'Source'),
    sortable: false,
  },
  {
    name: 'grossValue',
    type: 'number',
    header: i18n.t('modules.invoices.GrossValue', 'Gross [PLN]'),
    textAlign: 'end',
  },
  {
    name: 'taxValue',
    type: 'number',
    header: i18n.t('modules.invoices.TaxValue', 'Tax [PLN]'),
    textAlign: 'end',
  },
  {
    name: 'netValue',
    type: 'number',
    header: i18n.t('modules.invoices.NetValue', 'Net [PLN]'),
    textAlign: 'end',
    sortable: false,
  },
];
const InvoicesSummary = () => {
  const { t } = useTranslation();
  const summary = useSelector(selectSummary);
  const count = useSelector(selectAllRowsCount);
  const { grossValue: grossSummary, netValue: netSummary, taxValue: taxSummary } = summary;
  return (
    <VStack>
      <HStack alignSelf="stretch">
        <Spacer />
        <Tooltip
          label={`${t('modules.invoices.SummaryInfoPrefix', 'Summary contains sum of all')} ${count} ${t(
            'modules.invoices.SummaryInfoPostfix',
            'rows',
          )}`}
        >
          <HStack alignSelf="stretch">
            <Heading size="sm">{t('modules.invoices.Summary', 'Summary [PLN]:')}</Heading>
            <Spacer />
            <Heading size="sm">{t('modules.invoices.GrossSummary', 'gross:')}</Heading>
            <Heading size="md">{(grossSummary ?? 0).toFixed(2)}</Heading>
            <Spacer />
            <Heading size="sm">{t('modules.invoices.TaxSummary', 'tax:')}</Heading>
            <Heading size="md">{(taxSummary ?? 0).toFixed(2)}</Heading>
            <Spacer />
            <Heading size="sm">{t('modules.invoices.NetSummary', 'net:')}</Heading>
            <Heading size="md">{(netSummary ?? 0).toFixed(2)}</Heading>
          </HStack>
        </Tooltip>
      </HStack>
    </VStack>
  );
};
// eslint-disable-next-line react/prop-types
const Invoices = ({ height, contractorId, upAdmin }) => {
  const [reload, setReload] = useState(0);
  const [selectedInvoiceIsInNsei, setSelectedInvoiceIsInNsei] = useState(false);
  const [selectedInvoiceTypeId, setSelectedInvoiceTypeId] = useState(null);
  const [selectedInvoiceData, setSelectedInvoiceData] = useState(null);
  const dispatch = useDispatch();
  const [filter, setFilter] = useState({});
  const savedSelectedInvoiceIdRef = useRef(null);
  const allRowsCount = useSelector(selectAllRowsCount);
  const onSelectedIdChange = useCallback(
    (selectedId) => {
      dispatch(setSelectedId(selectedId));
    },
    [dispatch],
  );
  const onSelectedDataChange = useCallback(
    (data) => {
      if (data?.invoiceNo != null) {
        dispatch(setSelectedNo(data.invoiceNo));
      }
      setSelectedInvoiceIsInNsei(data?.invoiceNseiStatus === 0 && data?.parsedInvoiceNseiData?.invoiceStatus === 200);
      dispatch(setSelectedEmailSent(data?.emailSent === true));
      setSelectedInvoiceTypeId(data?.typeId ?? null);
      setSelectedInvoiceData(data ?? null);
    },
    [dispatch],
  );
  const accountRoles = useSelector(selectAccountRoles);
  const dataSource = useCallback(loadData(dispatch)({ contractorId, filter }), [
    contractorId,
    filter,
    dispatch,
    reload,
  ]);
  const selectedInvoiceId = useSelector(selectSelectedId);
  const { login } = useSelector(selectContractorView) ?? {};
  const admin = upAdmin === false ? includesSome(['admin', 'backoffice'], accountRoles) && login == null : true;

  const handleModalClose = useCallback((invoiceId) => {
    savedSelectedInvoiceIdRef.current = invoiceId;
  }, []);

  useEffect(() => {
    if (savedSelectedInvoiceIdRef.current != null && reload > 0 && allRowsCount > 0) {
      const timer = setTimeout(() => {
        if (savedSelectedInvoiceIdRef.current != null) {
          dispatch(setSelectedId(savedSelectedInvoiceIdRef.current));
          savedSelectedInvoiceIdRef.current = null;
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [reload, allRowsCount, dispatch]);

  return (
    <VStack alignItems="stretch">
      <HStack alignItems="flex-start">
        <InvoicesFilterBar
          admin={admin}
          filter={filter}
          setFilter={setFilter}
          contractorId={contractorId}
          selectedInvoiceIsInNsei={selectedInvoiceIsInNsei}
          selectedInvoiceTypeId={selectedInvoiceTypeId}
          selectedInvoiceData={selectedInvoiceData}
          reload={reload}
          setReload={setReload}
          onModalClose={handleModalClose}
        />
        <Divider orientation="vertical" />
        <DataGrid
          height={height}
          dataSource={dataSource}
          columns={defaultColumns({ admin })}
          onSelectedIdChange={onSelectedIdChange}
          onSelectedDataChange={onSelectedDataChange}
          pagination="remote"
          skip={0}
          limit={50}
          sortInfo={{ name: 'invoiceDate', dir: -1 }}
          allowUnsort={false}
          idProperty="invoiceId"
          selectedId={selectedInvoiceId}
        />
      </HStack>
      <InvoicesSummary />
    </VStack>
  );
};
export default Invoices;
